/**
 * Installable app: service worker registration, the update path, and the
 * browser's install prompt. The worker itself is public/sw.js.
 *
 * A new deploy ships a new worker, which takes over in the background. The
 * page then reloads at a quiet moment: when the app is next hidden (switched
 * away from, phone locked) or on the next in-app navigation, never mid-form.
 */
import { useSyncExternalStore } from 'react';

interface Hooks {
  /** Open a path inside the app (a tapped notification). */
  navigate: (path: string) => void;
  /** A push arrived while the app is open. */
  onPush: () => void;
  /** Called with a callback that runs before each in-app navigation. */
  beforeNavigate: (fn: (href: string) => void) => unknown;
}

let registration: Promise<ServiceWorkerRegistration | null> = Promise.resolve(null);

/** The active registration, or null where service workers are unavailable. */
export const serviceWorker = () => registration;

const inApp = (url: unknown): url is string => typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');

export function registerServiceWorker(hooks: Hooks) {
  if (!('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  const hadController = !!sw.controller;
  let updateReady = false;
  let lastCheck = Date.now();

  // After the page's own loading, so the worker's first install does not compete with it.
  const loaded = document.readyState === 'complete' ? Promise.resolve() : new Promise<void>((r) => window.addEventListener('load', () => r(), { once: true }));
  registration = loaded
    .then(() => sw.register('/sw.js', { scope: '/', updateViaCache: 'none' }))
    .catch((err: unknown) => {
      console.warn('Service worker not registered', err);
      return null;
    });

  sw.addEventListener('controllerchange', () => {
    // The first install takes control of a page that is already current.
    if (hadController) updateReady = true;
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (updateReady) location.reload();
      return;
    }
    // Back in the foreground (an installed app is rarely closed): look for a new deploy now and then.
    if (Date.now() - lastCheck > 15 * 60_000) {
      lastCheck = Date.now();
      void registration.then((r) => r?.update()).catch(() => undefined);
    }
  });
  hooks.beforeNavigate((href) => {
    if (updateReady) location.assign(href);
  });

  sw.addEventListener('message', (e: MessageEvent<{ type?: string; url?: unknown }>) => {
    if (e.data?.type === 'hearth:navigate' && inApp(e.data.url)) hooks.navigate(e.data.url);
    if (e.data?.type === 'hearth:push') hooks.onPush();
  });
  sw.startMessages();
}

// ---------------------------------------------------------------------------
// Install prompt (Chrome, Edge, Samsung Internet; iOS has none)
// ---------------------------------------------------------------------------

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    emit();
  });
}

/** Whether the browser offers installing Hearth right now. */
export function useCanInstall(): boolean {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => !!deferred,
  );
}

export async function installApp(): Promise<boolean> {
  if (!deferred) return false;
  const e = deferred;
  deferred = null;
  emit();
  await e.prompt();
  return (await e.userChoice).outcome === 'accepted';
}

// ---------------------------------------------------------------------------
// Platform
// ---------------------------------------------------------------------------

/** iPhone and iPad (iPadOS reports itself as a Mac with touch). */
export const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** Running from the home screen / as an installed app rather than in a browser tab. */
export const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
